/**
 * Citation Verification Tool - Utility Functions
 */

import { createFileLogger } from '../../../utils/logger';

const log = createFileLogger('VERIFY_CITATIONS_UTILS');

/**
 * Parse citation numbers from a scope string like "12,15,27"
 */
export function parseCitationScope(scope: string): number[] | null {
  if (scope === 'all' || scope === 'suspicious') {
    return null;
  }
  
  const parts = scope.split(',').map(s => s.trim());
  const numbers: number[] = [];
  
  for (const part of parts) {
    const num = parseInt(part, 10);
    if (!isNaN(num) && num > 0) {
      numbers.push(num);
    }
  }
  
  return numbers.length > 0 ? numbers : null;
}

/**
 * Detect citation format from a sample of citations
 */
export function detectCitationFormat(citations: string[]): 'numbered' | 'author_year' | 'mixed' | 'unknown' {
  if (citations.length === 0) return 'unknown';
  
  let numberedCount = 0;
  let authorYearCount = 0;
  
  for (const citation of citations) {
    // Check for numbered: [1], [12], (1), (12)
    if (/^\[?\d+\]?$/.test(citation.trim())) {
      numberedCount++;
    }
    // Check for author-year: (Smith, 2017), Smith et al. 2017
    else if (/\([A-Z][a-z]+.*\d{4}\)/.test(citation) || /[A-Z][a-z]+.*\d{4}/.test(citation)) {
      authorYearCount++;
    }
  }
  
  const total = citations.length;
  const numberedPercent = numberedCount / total;
  const authorYearPercent = authorYearCount / total;
  
  if (numberedPercent > 0.8) return 'numbered';
  if (authorYearPercent > 0.8) return 'author_year';
  if (numberedPercent > 0.2 && authorYearPercent > 0.2) return 'mixed';
  
  return 'unknown';
}

/**
 * Extract DOI from various formats
 */
export function extractDOI(text: string): string | null {
  // DOI patterns
  const doiPatterns = [
    /doi:\s*(10\.\d{4,}\/[^\s]+)/i,
    /https?:\/\/doi\.org\/(10\.\d{4,}\/[^\s]+)/i,
    /(10\.\d{4,}\/[^\s]+)/
  ];
  
  for (const pattern of doiPatterns) {
    const match = text.match(pattern);
    if (match) {
      return match[1] || match[0];
    }
  }
  
  return null;
}

/**
 * Extract PMID from text
 */
export function extractPMID(text: string): string | null {
  const pmidPattern = /PMID:\s*(\d+)/i;
  const match = text.match(pmidPattern);
  return match ? match[1] : null;
}

/**
 * Extract arXiv ID from text
 */
export function extractArxivId(text: string): string | null {
  const arxivPattern = /arxiv:\s*(\d{4}\.\d{4,5})/i;
  const match = text.match(arxivPattern);
  return match ? match[1] : null;
}

/**
 * Normalize author name for comparison
 */
export function normalizeAuthorName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[.,\-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Parse citation key to extract author names and year
 * Handles formats like:
 * - "(Smith, 2020)"
 * - "(Smith et al., 2020)"
 * - "(Smith & Jones, 2020)"
 * - "(Manthey et al., 2022; WHO, 2023)"
 */
export function parseCitationKey(citationKey: string): { authors: string[]; year?: number } {
  const authors: string[] = [];
  let year: number | undefined;
  
  // Remove parentheses and brackets
  const cleaned = citationKey.replace(/[\[\]()]/g, '').trim();
  
  // Split by semicolon for multiple citations
  const parts = cleaned.split(';').map(p => p.trim());
  
  // Process each part
  for (const part of parts) {
    // Extract year (4-digit number, possibly with letter suffix like 2024a)
    const yearMatch = part.match(/\b(19|20)\d{2}[a-z]?/);
    if (yearMatch) {
      // Extract just the numeric part
      const yearStr = yearMatch[0].match(/\d{4}/)?.[0];
      if (yearStr) {
        const parsedYear = parseInt(yearStr, 10);
        if (!year || parsedYear > year) {
          year = parsedYear;
        }
      }
    }
    
    // Extract author names (before the year)
    const beforeYear = part.split(/\b(19|20)\d{2}[a-z]?/)[0].trim();
    if (beforeYear) {
      // Handle "et al."
      const etAlMatch = beforeYear.match(/^([^,]+)\s+et\s+al\.?/i);
      if (etAlMatch) {
        authors.push(etAlMatch[1].trim());
      } else {
        // Handle "&" or "and"
        const authorParts = beforeYear.split(/\s+(&|and)\s+/i);
        for (const authorPart of authorParts) {
          if (authorPart !== '&' && authorPart !== 'and') {
            // Remove trailing commas and clean
            const author = authorPart.replace(/,$/, '').trim();
            if (author && author.length > 1 && !authors.includes(author)) {
              authors.push(author);
            }
          }
        }
      }
    }
  }
  
  return { authors, year };
}

/**
 * Calculate similarity between two strings (0-1)
 * Using simple Levenshtein distance
 */
export function stringSimilarity(str1: string, str2: string): number {
  const s1 = str1.toLowerCase();
  const s2 = str2.toLowerCase();
  
  const costs: number[] = [];
  for (let i = 0; i <= s1.length; i++) {
    let lastValue = i;
    for (let j = 0; j <= s2.length; j++) {
      if (i === 0) {
        costs[j] = j;
      } else if (j > 0) {
        let newValue = costs[j - 1];
        if (s1.charAt(i - 1) !== s2.charAt(j - 1)) {
          newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1;
        }
        costs[j - 1] = lastValue;
        lastValue = newValue;
      }
    }
    if (i > 0) {
      costs[s2.length] = lastValue;
    }
  }
  
  const maxLength = Math.max(s1.length, s2.length);
  return maxLength === 0 ? 1 : 1 - costs[s2.length] / maxLength;
}

/**
 * Analyze claim strength from language used
 */
export function analyzeClaimStrength(text: string): 'strong' | 'moderate' | 'weak' {
  const lowerText = text.toLowerCase();
  
  // Strong language indicators
  const strongWords = ['prove', 'proves', 'demonstrated', 'establishes', 'conclusively', 'definitively', 'always', 'never', 'causes', 'cures'];
  const hasStrongWords = strongWords.some(word => lowerText.includes(word));
  
  // Weak language indicators
  const weakWords = ['suggest', 'suggests', 'may', 'might', 'could', 'possibly', 'potentially', 'appears', 'seems', 'indicates'];
  const hasWeakWords = weakWords.some(word => lowerText.includes(word));
  
  if (hasStrongWords) return 'strong';
  if (hasWeakWords) return 'weak';
  return 'moderate';
}

/**
 * Truncate text with ellipsis
 */
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3) + '...';
}

/**
 * Format a list of authors for display
 */
export function formatAuthors(authors: string[], maxAuthors: number = 3): string {
  if (authors.length === 0) return '';
  if (authors.length <= maxAuthors) {
    return authors.join(', ');
  }
  return `${authors.slice(0, maxAuthors).join(', ')} et al.`;
}

/**
 * Create a unique ID for a citation
 */
export function createCitationId(citationKey: string, index: number): string {
  return `cite_${citationKey.replace(/[^a-zA-Z0-9]/g, '_')}_${index}`;
}

/**
 * Create a unique ID for a reference
 */
export function createReferenceId(index: number): string {
  return `ref_${index}`;
}

/**
 * Estimate token count (rough approximation)
 */
export function estimateTokens(text: string): number {
  // Rough estimate: ~4 characters per token
  return Math.ceil(text.length / 4);
}

/**
 * Format execution time for display
 */
export function formatExecutionTime(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
}

